import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface SearchConfig {
  id: string;
  name: string;
  mode: string;
  sources: string;
  reddit_max_threads: number;
  reddit_max_comments: number;
  reddit_max_depth: number;
  web_max_pages: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface SearchRun {
  id: string;
  config_id: string | null;
  query: string;
  slug: string;
  mode: string;
  sources: string;
  status: string;
  synthesis_text?: string | null;
  synthesis_path: string | null;
  stats: Record<string, unknown> | null;
  created_at: string;
}

export interface SearchRunRawDump {
  id: string;
  run_id: string;
  filename: string;
  content: string;
  sort_order: number;
}

export interface SearchRunWithDumps extends SearchRun {
  raw_dumps: SearchRunRawDump[];
}

export interface SearchRunsPage {
  items: SearchRun[];
  nextCursor: string | null;
}

export function useSearchConfigs() {
  return useQuery<{ items: SearchConfig[] }>({
    queryKey: ["search-configs"],
    queryFn: () => apiFetch<{ items: SearchConfig[] }>("/api/dev/search/configs"),
    refetchInterval: 30_000,
  });
}

export function useCreateSearchConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: Partial<SearchConfig> & { name: string }) =>
      apiFetch<{ ok: boolean; config: SearchConfig }>("/api/dev/search/configs", {
        method: "POST",
        body: JSON.stringify(fields),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-configs"] });
    },
  });
}

export function useUpdateSearchConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<SearchConfig> }) =>
      apiFetch<{ ok: boolean; config: SearchConfig }>(`/api/dev/search/configs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-configs"] });
    },
  });
}

export function useDeleteSearchConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/dev/search/configs/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-configs"] });
    },
  });
}

export function useSetDefaultConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; config: SearchConfig }>(`/api/dev/search/configs/${id}/set-default`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-configs"] });
    },
  });
}

export function useSearchRuns(limit: number = 20) {
  return useInfiniteQuery<SearchRunsPage>({
    queryKey: ["search-runs"],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (pageParam) params.set("cursor", pageParam as string);
      return apiFetch<SearchRunsPage>(`/api/dev/search/runs?${params.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
}

export function useSearchRunDetail(id: string | null) {
  return useQuery<SearchRunWithDumps>({
    queryKey: ["search-run-detail", id],
    queryFn: () => apiFetch<SearchRunWithDumps>(`/api/dev/search/runs/${id}`),
    enabled: !!id,
  });
}
